import { LitElement, html } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { live } from 'lit/directives/live.js';
import { currentLang, t } from '../i18n';
import type { AuthPanelState } from './shared';
import { defineZenComponent } from './register';

interface AuthDraft {
  loginEmail: string;
  loginPassword: string;
  loginCode: string;
  resetEmail: string;
  signupName: string;
  signupEmail: string;
  signupPassword: string;
  agreeTerms: boolean;
  newPassword: string;
  newPasswordConfirm: string;
}

const defaultDraft = (): AuthDraft => ({
  loginEmail: '',
  loginPassword: '',
  loginCode: '',
  resetEmail: '',
  signupName: '',
  signupEmail: '',
  signupPassword: '',
  agreeTerms: false,
  newPassword: '',
  newPasswordConfirm: '',
});

const TERMS_URL = 'https://zenmetry.notion.site/Zenmetry-be1d1b81e56a4d0ca3206a4e3a5cdca4';
const PRIVACY_URL = 'https://zenmetry.notion.site/a22e1e53384c4eefa5fe693540fd6bcb';

export class ZenAuthPanel extends LitElement {
  static properties = {
    state: { attribute: false },
    draft: { attribute: false },
  };

  declare state: AuthPanelState;
  declare draft: AuthDraft;

  private readonly handleLangChange = () => {
    this.requestUpdate();
  };

  constructor() {
    super();
    this.state = {
      tab: 'login',
      loginPending: false,
      signupPending: false,
      resetPending: false,
      updatePending: false,
      resetLinkError: null,
      inviteNotice: false,
      invitedEmail: null,
      confirmNotice: false,
      signupSentTo: null,
      resetSentTo: null,
      mfaStep: false,
    };
    this.draft = defaultDraft();
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this.handleLangChange);
  }

  disconnectedCallback() {
    document.removeEventListener('zen-lang-change', this.handleLangChange);
  }

  private emit(name: string) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  render() {
    const isReset = this.state.tab === 'reset' || this.state.tab === 'update-password';
    return keyed(currentLang, html`
      <div class="card">
        ${this.state.confirmNotice ? html`
          <div id="authConfirmNotice" role="status" style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;margin-bottom:12px;border-radius:var(--radius);background:color-mix(in srgb, var(--accent) 8%, transparent);font-size:12px;line-height:1.5;">
            <span class="mi mi-sm" style="color:var(--accent);" aria-hidden="true">mark_email_read</span>
            <span>${t('confirmEmailEnterPassword')}</span>
          </div>
        ` : ''}
        ${this.state.inviteNotice ? html`
          <div id="authInviteNotice" role="status" style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;margin-bottom:12px;border-radius:var(--radius);background:color-mix(in srgb, var(--accent) 8%, transparent);font-size:12px;line-height:1.5;">
            <span class="mi mi-sm" style="color:var(--accent);" aria-hidden="true">group_add</span>
            <span>${t('authInviteNotice')}</span>
          </div>
        ` : ''}
        <div class="auth-tabs" style=${isReset ? 'display:none' : ''}>
          <button class="auth-tab ${this.state.tab === 'login' ? 'active' : ''}" @click=${() => this.emit('auth-tab-login')}>${t('login')}</button>
          <button class="auth-tab ${this.state.tab === 'signup' ? 'active' : ''}" @click=${() => this.emit('auth-tab-signup')}>${t('signupTab')}</button>
        </div>

        <form class="auth-form ${this.state.tab === 'login' ? 'active' : ''}" id="loginForm" autocomplete="on">
          <div class="field">
            <label for="loginEmail">${t('email')}</label>
            <input type="email" id="loginEmail" name="email" autocomplete="username" required placeholder="user@example.com" .value=${live(this.draft.loginEmail)}>
          </div>
          <div class="field">
            <label for="loginPassword">${t('password')}</label>
            <input type="password" id="loginPassword" name="password" autocomplete="current-password" required placeholder=${t('password')} .value=${live(this.draft.loginPassword)}>
          </div>
          ${this.state.mfaStep ? html`
            <div class="field">
              <label for="loginCode">${t('mfaCodeLabel')}</label>
              <div role="status" style="display:flex;align-items:flex-start;gap:6px;font-size:12px;line-height:1.5;color:var(--text-muted);margin-bottom:6px;">
                <span class="mi mi-sm" style="color:var(--accent);" aria-hidden="true">phonelink_lock</span>
                <span>${t('mfaCodeHint')}</span>
              </div>
              <input type="text" id="loginCode" name="one-time-code" autocomplete="one-time-code" inputmode="numeric" pattern="[0-9 ]*" maxlength="7" required placeholder="123456" .value=${live(this.draft.loginCode)}>
            </div>
          ` : ''}
          <div class="msg msg-error" id="loginError"></div>
          <button type="submit" class="btn btn-primary" id="loginBtn" ?disabled=${this.state.loginPending}>
            ${this.state.loginPending ? html`<span class="spinner spinner-white"></span>` : t('login')}
          </button>
          <div style="text-align:center;margin-top:4px;">
            <a href="#" style="font-size:12px;color:var(--text-muted);text-decoration:none;" @click=${(event: Event) => { event.preventDefault(); this.emit('auth-tab-reset'); }}>${t('forgotPassword')}</a>
          </div>
          <p id="loginTermsLinks" style="font-size:11px;color:var(--text-muted);text-align:center;margin-top:12px;">
            <a href=${TERMS_URL} target="_blank" rel="noopener" style="color:var(--text-muted);">${t('agreeTermsLink')}</a>
            <span> ・ </span>
            <a href=${PRIVACY_URL} target="_blank" rel="noopener" style="color:var(--text-muted);">${t('agreePrivacyLink')}</a>
          </p>
        </form>

        ${this.state.tab === 'reset' && this.state.resetSentTo !== null ? html`
          <div class="auth-form active" id="resetSent" role="status">
            <div style="display:flex;align-items:flex-start;gap:8px;font-size:13px;line-height:1.6;">
              <span class="mi" style="color:var(--success);" aria-hidden="true">mark_email_read</span>
              <span>${t('resetEmailSentTo', { email: this.state.resetSentTo })}</span>
            </div>
            <div style="text-align:center;margin-top:12px;">
              <a href="#" style="font-size:12px;color:var(--text-muted);text-decoration:none;" @click=${(event: Event) => { event.preventDefault(); this.emit('auth-tab-login'); }}>${t('backToLogin')}</a>
            </div>
          </div>
        ` : ''}
        <form class="auth-form ${this.state.tab === 'reset' && this.state.resetSentTo === null ? 'active' : ''}" id="resetForm" autocomplete="on">
          <div style="font-size:13px;color:var(--text-secondary);margin-bottom:4px;">${t('resetDescription')}</div>
          <div class="field">
            <label for="resetEmail">${t('email')}</label>
            <input type="email" id="resetEmail" name="email" autocomplete="username" required placeholder="user@example.com" .value=${live(this.draft.resetEmail)}>
          </div>
          ${this.state.resetLinkError ? html`
            <div class="msg msg-error show" id="resetLinkError" role="alert">
              <span class="mi mi-sm" style="vertical-align:-3px;" aria-hidden="true">error</span> ${t(this.state.resetLinkError === 'invalid' ? 'resetLinkInvalid' : 'resetLinkUnverified')}
            </div>
          ` : ''}
          <div class="msg msg-error" id="resetError"></div>
          <button type="submit" class="btn btn-primary" id="resetBtn" style="width:100%;" ?disabled=${this.state.resetPending}>${this.state.resetPending ? html`<span class="spinner spinner-white"></span>` : t('resetBtn')}</button>
          <div style="text-align:center;margin-top:4px;">
            <a href="#" style="font-size:12px;color:var(--text-muted);text-decoration:none;" @click=${(event: Event) => { event.preventDefault(); this.emit('auth-tab-login'); }}>${t('backToLogin')}</a>
          </div>
        </form>

        ${this.state.tab === 'signup' && this.state.signupSentTo !== null ? html`
          <div class="auth-form active" id="signupSent" role="status">
            <div style="display:flex;align-items:flex-start;gap:8px;font-size:13px;line-height:1.6;">
              <span class="mi" style="color:var(--success);" aria-hidden="true">mark_email_read</span>
              <span>${t('signupConfirmEmailSentTo', { email: this.state.signupSentTo })}</span>
            </div>
            <div style="text-align:center;margin-top:12px;">
              <a href="#" style="font-size:12px;color:var(--text-muted);text-decoration:none;" @click=${(event: Event) => { event.preventDefault(); this.emit('auth-tab-login'); }}>${t('backToLogin')}</a>
            </div>
          </div>
        ` : ''}
        <form class="auth-form ${this.state.tab === 'signup' && this.state.signupSentTo === null ? 'active' : ''}" id="signupForm" autocomplete="on">
          <div class="field">
            <label for="signupName">${t('signupName')}</label>
            <input type="text" id="signupName" name="name" autocomplete="name" required placeholder=${t('signupNamePlaceholder')} .value=${live(this.draft.signupName)}>
          </div>
          <div class="field">
            <label for="signupEmail">${t('email')}</label>
            <input type="email" id="signupEmail" name="email" autocomplete="username" required placeholder="user@example.com" .value=${live(this.state.invitedEmail ?? this.draft.signupEmail)} ?readonly=${this.state.invitedEmail !== null}>
          </div>
          <div class="field">
            <label for="signupPassword">${t('password')}</label>
            <input type="password" id="signupPassword" name="password" autocomplete="new-password" required minlength="12" placeholder=${t('signupPasswordPlaceholder')} .value=${live(this.draft.signupPassword)}>
            <div id="passwordHints" style="font-size:11px;margin-top:4px;line-height:1.8;display:none;">
              <span id="pwHintLength" style="color:var(--text-muted)" data-i18n-hint="pwHintLength">・${t('pwHintLength')}</span>
            </div>
          </div>
          <div class="msg msg-error" id="signupError"></div>
          <label style="display:flex;align-items:center;gap:6px;font-weight:normal;cursor:pointer;margin:4px 0 0;">
            <input type="checkbox" id="agreeTerms" .checked=${live(this.draft.agreeTerms)}>
            <span id="agreeTermsText" style="font-size:11px;color:var(--text-secondary);line-height:1.4;">
              ${t('agreeTermsPrefix')}
              <a href=${TERMS_URL} target="_blank" rel="noopener">${t('agreeTermsLink')}</a>
              ${t('agreeTermsAnd')}
              <a href=${PRIVACY_URL} target="_blank" rel="noopener">${t('agreePrivacyLink')}</a>
              ${t('agreeTermsSuffix')}
            </span>
          </label>
          <button type="submit" class="btn btn-primary" id="signupBtn" ?disabled=${this.state.signupPending}>${this.state.signupPending ? html`<span class="spinner spinner-white"></span>` : t('signupBtn')}</button>
        </form>

        <form class="auth-form ${this.state.tab === 'update-password' ? 'active' : ''}" id="updatePasswordForm">
          <div style="font-size:13px;color:var(--text-secondary);margin-bottom:4px;">${t('updatePasswordDescription')}</div>
          <div class="field">
            <label for="newPassword">${t('newPassword')}</label>
            <input type="password" id="newPassword" name="password" autocomplete="new-password" required minlength="12" placeholder=${t('signupPasswordPlaceholder')} .value=${live(this.draft.newPassword)}>
            <div id="updatePwHints" style="font-size:11px;margin-top:4px;line-height:1.8;display:none;">
              <span id="updatePwHintLength" style="color:var(--text-muted)" data-i18n-hint="pwHintLength">・${t('pwHintLength')}</span><br>
              <span id="updatePwHintMatch" style="color:var(--text-muted)" data-i18n-hint="pwHintMatch">・${t('pwHintMatch')}</span>
            </div>
          </div>
          <div class="field">
            <label for="newPasswordConfirm">${t('newPasswordConfirm')}</label>
            <input type="password" id="newPasswordConfirm" autocomplete="new-password" required minlength="12" placeholder=${t('newPasswordConfirmPlaceholder')} .value=${live(this.draft.newPasswordConfirm)}>
          </div>
          <div class="msg msg-error" id="updatePasswordError"></div>
          <div class="msg msg-success" id="updatePasswordSuccess"></div>
          <button type="submit" class="btn btn-primary" id="updatePasswordBtn" style="width:100%;" ?disabled=${this.state.updatePending}>${this.state.updatePending ? html`<span class="spinner spinner-white"></span>` : t('updatePasswordBtn')}</button>
        </form>
      </div>
    `);
  }
}

defineZenComponent('zen-auth-panel', ZenAuthPanel);
